import * as THREE from 'three';
import {
  SAVANNA_SIGNAL_COLORS,
  createSavannaMaterials,
  disposeSavannaMaterials,
  type SavannaMaterials,
} from '../assets/MaterialLibrary';
import { collectModelDiagnostics, type ModelDiagnostics } from '../assets/ModelDiagnostics';

export type WaterholeVisual = {
  id: 'waterhole';
  root: THREE.Group;
  position: THREE.Vector3;
  radius: number;
  fillRadius: number;
  surfaceY: number;
};

export type FruitVisual = {
  id: string;
  root: THREE.Group;
  position: THREE.Vector3;
  radius: number;
  consumed: boolean;
  setConsumed: (consumed: boolean) => void;
};

export type SaplingVisual = {
  id: string;
  root: THREE.Group;
  position: THREE.Vector3;
  radius: number;
  revived: boolean;
  setRevived: (revived: boolean) => void;
};

export type HazardVisual = {
  id: string;
  kind: 'thorn-scrub';
  root: THREE.Group;
  position: THREE.Vector3;
  radius: number;
};

export type ThrowableVisual = {
  id: string;
  kind: 'rock' | 'branch' | 'seed-pod';
  root: THREE.Group;
  position: THREE.Vector3;
  radius: number;
  held: boolean;
  setHeld: (held: boolean) => void;
};

export type TargetRingVisual = {
  id: string;
  root: THREE.Group;
  position: THREE.Vector3;
  radius: number;
  hit: boolean;
  setHit: (hit: boolean) => void;
};

export type LandmarkVisual = {
  id: 'central-acacia' | 'western-baobab' | 'red-rock-ridge' | 'termite-field' | 'waterhole';
  root: THREE.Group;
  position: THREE.Vector3;
  radius: number;
};

export type SavannaWorldOptions = {
  materials?: SavannaMaterials;
  size?: number;
};

export class SavannaWorldVisuals {
  readonly root = new THREE.Group();
  readonly bounds: { halfWidth: number; halfDepth: number };
  readonly waterhole: WaterholeVisual;
  readonly fruits: FruitVisual[] = [];
  readonly saplings: SaplingVisual[] = [];
  readonly hazards: HazardVisual[] = [];
  readonly throwables: ThrowableVisual[] = [];
  readonly targetRings: TargetRingVisual[] = [];
  readonly landmarks: LandmarkVisual[] = [];

  private readonly materials: SavannaMaterials;
  private readonly ownsMaterials: boolean;
  private readonly geometries = new Set<THREE.BufferGeometry>();
  private readonly privateMaterials = new Set<THREE.Material>();
  private readonly animatedMaterials: THREE.ShaderMaterial[] = [];
  private readonly fruitBaseY = new Map<FruitVisual, number>();
  private readonly revivedCrowns: THREE.Group[] = [];

  constructor(options: SavannaWorldOptions = {}) {
    const size = options.size ?? 120;
    this.bounds = { halfWidth: size * 0.5, halfDepth: size * 0.5 };
    this.materials = options.materials ?? createSavannaMaterials();
    this.ownsMaterials = !options.materials;
    this.root.name = 'savannaWorld';

    this.root.add(this.createSky());
    this.root.add(this.createTerrain(size));
    this.waterhole = this.createWaterhole();
    this.root.add(this.waterhole.root);

    const centralAcacia = this.createAcacia('centralAcacia', new THREE.Vector3(0, 0, 3), 1.12);
    this.root.add(centralAcacia);
    this.landmarks.push({ id: 'central-acacia', root: centralAcacia, position: centralAcacia.position, radius: 8 });

    const baobab = this.createBaobab(new THREE.Vector3(-37, 0, 4));
    this.root.add(baobab);
    this.landmarks.push({ id: 'western-baobab', root: baobab, position: baobab.position, radius: 10 });

    this.createSupportingTrees();
    this.createFruitClusters();
    this.createSaplings();
    this.createThornScrub();
    this.createThrowables();
    this.createRedRockRidge();
    this.createTermiteField();
    this.createTargetRings();
    this.root.add(this.createGrassField(440));
  }

  sampleGroundHeight(x: number, z: number): number {
    return sampleSavannaGroundHeight(x, z);
  }

  update(delta: number, elapsed: number): void {
    for (const material of this.animatedMaterials) {
      material.uniforms.uTime.value = elapsed;
    }

    for (const fruit of this.fruits) {
      if (fruit.consumed) continue;
      const baseY = this.fruitBaseY.get(fruit) ?? fruit.root.position.y;
      fruit.root.position.y = baseY + Math.sin(elapsed * 1.8 + fruit.position.x * 0.17) * 0.045;
      fruit.root.rotation.y += delta * 0.32;
    }

    for (const crown of this.revivedCrowns) {
      crown.rotation.y = Math.sin(elapsed * 0.7 + crown.position.x) * 0.035;
    }

    for (const target of this.targetRings) {
      if (target.hit) continue;
      const pulse = 1 + Math.sin(elapsed * 2.1 + target.position.x) * 0.025;
      target.root.scale.setScalar(pulse);
    }
  }

  reset(): void {
    for (const fruit of this.fruits) fruit.setConsumed(false);
    for (const sapling of this.saplings) sapling.setRevived(false);
    for (const throwable of this.throwables) throwable.setHeld(false);
    for (const target of this.targetRings) target.setHit(false);
  }

  diagnostics(): ModelDiagnostics {
    return collectModelDiagnostics(this.root);
  }

  dispose(): void {
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.privateMaterials) material.dispose();
    if (this.ownsMaterials) disposeSavannaMaterials(this.materials);
    this.root.clear();
  }

  private createSky(): THREE.Mesh {
    const material = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        uTop: { value: new THREE.Color(0x4f89b5) },
        uHorizon: { value: new THREE.Color(0xf4c889) },
        uSunColor: { value: new THREE.Color(0xffedbc) },
        uSunDir: { value: new THREE.Vector3(-0.45, 0.24, -0.58).normalize() },
      },
      vertexShader: `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vDir;
        uniform vec3 uTop;
        uniform vec3 uHorizon;
        uniform vec3 uSunColor;
        uniform vec3 uSunDir;
        void main() {
          float h = clamp(vDir.y * 0.5 + 0.5, 0.0, 1.0);
          vec3 color = mix(uHorizon, uTop, pow(h, 0.72));
          float sun = clamp(dot(normalize(vDir), normalize(uSunDir)), 0.0, 1.0);
          color += uSunColor * (pow(sun, 700.0) + pow(sun, 9.0) * 0.16);
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });
    this.privateMaterials.add(material);
    const sky = new THREE.Mesh(this.track(new THREE.SphereGeometry(155, 28, 15)), material);
    sky.name = 'savannaSky';
    sky.frustumCulled = false;
    return sky;
  }

  private createTerrain(size: number): THREE.Mesh {
    const segments = 54;
    const geometry = this.track(new THREE.PlaneGeometry(size, size, segments, segments));
    geometry.rotateX(-Math.PI / 2);
    const positions = geometry.attributes.position;
    const colors: number[] = [];
    const sand = new THREE.Color(0xb88c51);
    const ochre = new THREE.Color(0xc89d5a);
    const dry = new THREE.Color(0xa77643);
    for (let index = 0; index < positions.count; index += 1) {
      const x = positions.getX(index);
      const z = positions.getZ(index);
      positions.setY(index, sampleSavannaGroundHeight(x, z));
      const noise = Math.sin(x * 0.37 + z * 0.21) * 0.5 + 0.5;
      const color = sand.clone().lerp(ochre, noise * 0.44).lerp(dry, Math.max(0, sampleSavannaGroundHeight(x, z)) * 0.08);
      colors.push(color.r, color.g, color.b);
    }
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 0.98,
      metalness: 0,
    });
    this.privateMaterials.add(material);
    const terrain = new THREE.Mesh(geometry, material);
    terrain.name = 'rollingSavannaTerrain';
    terrain.receiveShadow = true;
    return terrain;
  }

  private createWaterhole(): WaterholeVisual {
    const position = new THREE.Vector3(29, -0.52, -28);
    const root = new THREE.Group();
    root.name = 'waterhole';
    root.position.copy(position);

    const mud = this.mesh(new THREE.RingGeometry(8.2, 12.4, 64), this.materials.earthDark, 'waterholeMud');
    mud.rotation.x = -Math.PI / 2;
    mud.scale.y = 0.72;
    mud.receiveShadow = true;
    root.add(mud);

    const rim = this.mesh(new THREE.TorusGeometry(10.3, 0.42, 7, 64), this.materials.earth, 'waterholeRim');
    rim.rotation.x = Math.PI / 2;
    rim.scale.y = 0.72;
    root.add(rim);

    const waterMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uDeep: { value: new THREE.Color(0x226d78) },
        uShallow: { value: new THREE.Color(0x67c3c2) },
        uSun: { value: new THREE.Color(0xffe1a0) },
      },
      vertexShader: `
        uniform float uTime;
        varying vec2 vUv;
        varying float vWave;
        void main() {
          vUv = uv;
          vec3 p = position;
          vWave = sin(p.x * 1.7 + uTime * 1.2) * cos(p.y * 1.35 - uTime) * 0.035;
          p.z += vWave;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec3 uDeep;
        uniform vec3 uShallow;
        uniform vec3 uSun;
        varying vec2 vUv;
        varying float vWave;
        void main() {
          float edge = smoothstep(0.55, 0.98, distance(vUv, vec2(0.5)) * 2.0);
          float ripple = sin((vUv.x + vUv.y) * 34.0 + uTime * 1.7) * 0.035;
          vec3 color = mix(uDeep, uShallow, edge + ripple + vWave * 2.0);
          color += uSun * max(0.0, ripple) * 0.13;
          gl_FragColor = vec4(color, 0.88);
        }
      `,
    });
    this.privateMaterials.add(waterMaterial);
    this.animatedMaterials.push(waterMaterial);
    const water = new THREE.Mesh(this.track(new THREE.CircleGeometry(9.9, 64)), waterMaterial);
    water.name = 'waterSurface';
    water.rotation.x = -Math.PI / 2;
    water.scale.y = 0.72;
    water.position.y = 0.11;
    root.add(water);

    const stoneGeometry = this.track(new THREE.IcosahedronGeometry(1, 0));
    for (let index = 0; index < 18; index += 1) {
      const angle = index * 2.399963;
      const radius = 10.3 + (index % 3) * 0.72;
      const size = 0.44 + (index % 4) * 0.08;
      const stone = this.mesh(stoneGeometry, this.materials.stone, 'waterholeStone');
      stone.position.set(Math.cos(angle) * radius, 0.05, Math.sin(angle) * radius * 0.72);
      stone.scale.set(size, size * 0.62, size);
      stone.rotation.set(angle * 0.3, angle, 0);
      root.add(stone);
    }

    const landmark: LandmarkVisual = { id: 'waterhole', root, position, radius: 13 };
    this.landmarks.push(landmark);
    return { id: 'waterhole', root, position, radius: 10.5, fillRadius: 12.3, surfaceY: position.y + 0.11 };
  }

  private createAcacia(name: string, position: THREE.Vector3, scale: number): THREE.Group {
    const root = new THREE.Group();
    root.name = name;
    root.position.set(position.x, sampleSavannaGroundHeight(position.x, position.z), position.z);
    root.scale.setScalar(scale);

    const trunk = this.mesh(new THREE.CylinderGeometry(0.48, 0.72, 5.1, 9), this.materials.bark, 'acaciaTrunk');
    trunk.position.y = 2.55;
    root.add(trunk);

    const branchPoints: ReadonlyArray<readonly [number, number, number]> = [
      [-2.1, 6.1, 0.45],
      [2.15, 5.8, -0.3],
      [-1.4, 5.75, -1.25],
      [1.25, 6.2, 1.2],
    ];
    for (const [x, y, z] of branchPoints) {
      root.add(this.branchBetween(new THREE.Vector3(0, 4.45, 0), new THREE.Vector3(x, y, z), 0.22, this.materials.bark));
    }

    const crownGeometry = this.track(new THREE.SphereGeometry(1, 12, 8));
    const crownPositions: ReadonlyArray<readonly [number, number, number, number]> = [
      [-1.9, 6.15, 0.35, 1.8],
      [0, 6.45, 0, 2.15],
      [2.0, 6.05, -0.15, 1.75],
      [-0.4, 6.0, -1.45, 1.45],
      [0.7, 6.05, 1.35, 1.4],
    ];
    crownPositions.forEach(([x, y, z, width], index) => {
      const crown = this.mesh(crownGeometry, index % 2 === 0 ? this.materials.leaf : this.materials.leafLight, 'acaciaCanopy');
      crown.position.set(x, y, z);
      crown.scale.set(width, 0.58, width * 0.72);
      root.add(crown);
    });
    return root;
  }

  private createBaobab(position: THREE.Vector3): THREE.Group {
    const root = new THREE.Group();
    root.name = 'giantWesternBaobab';
    root.position.set(position.x, sampleSavannaGroundHeight(position.x, position.z), position.z);

    const points = [
      new THREE.Vector2(0.7, 0),
      new THREE.Vector2(1.35, 0.7),
      new THREE.Vector2(1.55, 2.5),
      new THREE.Vector2(1.25, 5.1),
      new THREE.Vector2(0.72, 7.2),
    ];
    const trunk = this.mesh(new THREE.LatheGeometry(points, 12), this.materials.barkLight, 'baobabTrunk');
    trunk.castShadow = true;
    root.add(trunk);

    const crown = new THREE.Group();
    crown.position.y = 6.6;
    const branchTips: ReadonlyArray<readonly [number, number, number]> = [
      [-2.8, 2.4, 0.2], [2.7, 2.1, 0.5], [-1.8, 2.8, -2.0], [1.4, 3.0, -1.7], [0.4, 2.6, 2.2],
    ];
    branchTips.forEach(([x, y, z]) => {
      crown.add(this.branchBetween(new THREE.Vector3(0, 0, 0), new THREE.Vector3(x, y, z), 0.31, this.materials.bark));
      const leaves = this.mesh(new THREE.SphereGeometry(1, 11, 7), this.materials.leaf, 'baobabCrown');
      leaves.position.set(x, y + 0.2, z);
      leaves.scale.set(1.65, 0.72, 1.4);
      crown.add(leaves);
    });
    root.add(crown);
    return root;
  }

  private createSupportingTrees(): void {
    const trees: ReadonlyArray<readonly [number, number, number]> = [
      [-24, -28, 0.72], [13, 31, 0.82], [42, 15, 0.68], [-43, -20, 0.78], [14, -8, 0.62],
    ];
    trees.forEach(([x, z, scale], index) => this.root.add(this.createAcacia(`acacia${index}`, new THREE.Vector3(x, 0, z), scale)));
  }

  private createFruitClusters(): void {
    const positions: ReadonlyArray<readonly [number, number, number]> = [
      [-1.8, 5.15, 1.7], [1.4, 5.3, 2.1], [2.5, 5.0, 3.6],
      [-39.1, 7.8, 4.8], [-35.5, 8.2, 2.5], [-34.8, 7.65, 5.8],
      [12.6, 4.55, 30], [14.5, 4.45, 31.8],
    ];
    const fruitGeometry = this.track(new THREE.SphereGeometry(0.28, 12, 9));
    const capGeometry = this.track(new THREE.ConeGeometry(0.11, 0.18, 6));
    positions.forEach(([x, y, z], index) => {
      const root = new THREE.Group();
      root.name = `fruit${index}`;
      root.position.set(x, y + sampleSavannaGroundHeight(x, z), z);
      const fruit = this.mesh(fruitGeometry, this.materials.fruit, 'fruitBody');
      fruit.scale.set(0.86, 1.16, 0.86);
      const cap = this.mesh(capGeometry, this.materials.leaf, 'fruitCap');
      cap.position.y = 0.35;
      root.add(fruit, cap);
      this.root.add(root);

      const visual: FruitVisual = {
        id: `fruit-${index}`,
        root,
        position: root.position,
        radius: 0.5,
        consumed: false,
        setConsumed: (consumed) => {
          visual.consumed = consumed;
          root.visible = !consumed;
        },
      };
      this.fruits.push(visual);
      this.fruitBaseY.set(visual, root.position.y);
    });
  }

  private createSaplings(): void {
    const positions: ReadonlyArray<readonly [number, number]> = [[-18, -22], [35, 13], [-5, 38]];
    const dryTrunkGeometry = this.track(new THREE.CylinderGeometry(0.12, 0.2, 2.35, 7));
    const healthyTrunkGeometry = this.track(new THREE.CylinderGeometry(0.13, 0.21, 2.48, 8));
    const leafGeometry = this.track(new THREE.SphereGeometry(0.32, 9, 6));
    const flowerGeometry = this.track(new THREE.IcosahedronGeometry(0.12, 0));
    positions.forEach(([x, z], index) => {
      const root = new THREE.Group();
      root.name = `drySapling${index}`;
      root.position.set(x, sampleSavannaGroundHeight(x, z), z);

      const dry = new THREE.Group();
      dry.name = 'dryState';
      const trunk = this.mesh(dryTrunkGeometry, this.materials.dryWood, 'saplingTrunk');
      trunk.position.y = 1.18;
      dry.add(trunk);
      dry.add(this.branchBetween(new THREE.Vector3(0, 1.45, 0), new THREE.Vector3(-0.75, 2.2, 0.08), 0.065, this.materials.dryWood));
      dry.add(this.branchBetween(new THREE.Vector3(0, 1.7, 0), new THREE.Vector3(0.72, 2.45, -0.12), 0.065, this.materials.dryWood));

      const revived = new THREE.Group();
      revived.name = 'revivedState';
      revived.visible = false;
      const healthyTrunk = this.mesh(healthyTrunkGeometry, this.materials.bark, 'healthySaplingTrunk');
      healthyTrunk.position.y = 1.24;
      revived.add(healthyTrunk);
      for (let leafIndex = 0; leafIndex < 7; leafIndex += 1) {
        const angle = (leafIndex / 7) * Math.PI * 2;
        const leaf = this.mesh(leafGeometry, leafIndex % 2 ? this.materials.leaf : this.materials.leafLight, 'newLeaf');
        leaf.scale.set(1.35, 0.54, 0.72);
        leaf.position.set(Math.cos(angle) * 0.72, 2.0 + (leafIndex % 3) * 0.3, Math.sin(angle) * 0.55);
        revived.add(leaf);
      }
      for (let flowerIndex = 0; flowerIndex < 4; flowerIndex += 1) {
        const flower = this.mesh(flowerGeometry, this.materials.flower, 'saplingBloom');
        flower.position.set(Math.cos(flowerIndex * 1.7) * 0.62, 2.55, Math.sin(flowerIndex * 1.7) * 0.45);
        revived.add(flower);
      }
      root.add(dry, revived);
      this.root.add(root);
      this.revivedCrowns.push(revived);

      const visual: SaplingVisual = {
        id: `sapling-${index}`,
        root,
        position: root.position,
        radius: 1.35,
        revived: false,
        setRevived: (isRevived) => {
          visual.revived = isRevived;
          dry.visible = !isRevived;
          revived.visible = isRevived;
        },
      };
      this.saplings.push(visual);
    });
  }

  private createThornScrub(): void {
    const patches: ReadonlyArray<readonly [number, number, number]> = [[-10, -10, 3.3], [18, 11, 4.3], [-19, 27, 3.7], [36, -4, 3.1]];
    const thornGeometry = this.track(new THREE.ConeGeometry(0.12, 0.75, 5));
    patches.forEach(([x, z, radius], patchIndex) => {
      const root = new THREE.Group();
      root.name = `thornScrub${patchIndex}`;
      root.position.set(x, sampleSavannaGroundHeight(x, z), z);
      for (let index = 0; index < 15; index += 1) {
        const angle = index * 2.399963;
        const distance = 0.7 + ((index * 37) % 11) / 11 * radius;
        const bush = this.mesh(new THREE.SphereGeometry(0.55, 7, 5), this.materials.thorn, 'thornBush');
        bush.scale.set(1.15, 0.66, 0.92);
        bush.position.set(Math.cos(angle) * distance, 0.42, Math.sin(angle) * distance);
        root.add(bush);
        if (index % 2 === 0) {
          const thorn = this.mesh(thornGeometry, this.materials.hazard, 'warningThorn');
          thorn.position.set(Math.cos(angle) * distance, 0.95, Math.sin(angle) * distance);
          thorn.rotation.z = Math.cos(angle) * 0.75;
          thorn.rotation.x = Math.sin(angle) * 0.75;
          root.add(thorn);
        }
      }
      this.root.add(root);
      this.hazards.push({ id: `thorn-${patchIndex}`, kind: 'thorn-scrub', root, position: root.position, radius });
    });
  }

  private createThrowables(): void {
    const specs: ReadonlyArray<readonly ['rock' | 'branch' | 'seed-pod', number, number]> = [
      ['branch', 3, -1], ['rock', 25, -20], ['seed-pod', -31, 8], ['rock', -12, 32],
      ['branch', 31, -38], ['seed-pod', 11, 29], ['rock', 42, 18], ['branch', -40, -15], ['seed-pod', 5.8, 7.8],
    ];
    specs.forEach(([kind, x, z], index) => {
      const root = new THREE.Group();
      root.name = `throwable${kind}${index}`;
      root.position.set(x, sampleSavannaGroundHeight(x, z) + 0.16, z);
      if (kind === 'rock') {
        const rock = this.mesh(new THREE.IcosahedronGeometry(0.38, 0), this.materials.stone, 'throwableRock');
        rock.scale.set(1.25, 0.72, 0.92);
        root.add(rock);
      } else if (kind === 'branch') {
        const branch = this.mesh(new THREE.CylinderGeometry(0.09, 0.14, 1.45, 7), this.materials.dryWood, 'throwableBranch');
        branch.rotation.z = Math.PI / 2;
        root.add(branch);
        const fork = this.mesh(new THREE.CylinderGeometry(0.055, 0.08, 0.62, 6), this.materials.dryWood, 'branchFork');
        fork.position.set(0.52, 0.21, 0);
        fork.rotation.z = -0.74;
        root.add(fork);
      } else {
        const pod = this.mesh(new THREE.CapsuleGeometry(0.16, 0.48, 4, 8), this.materials.reward, 'seedPod');
        pod.rotation.z = Math.PI / 2;
        root.add(pod);
      }
      this.root.add(root);
      const visual: ThrowableVisual = {
        id: `throwable-${index}`,
        kind,
        root,
        position: root.position,
        radius: kind === 'branch' ? 0.85 : 0.48,
        held: false,
        setHeld: (held) => {
          visual.held = held;
          root.traverse((object) => {
            if (object instanceof THREE.Mesh) object.castShadow = !held;
          });
        },
      };
      this.throwables.push(visual);
    });
  }

  private createRedRockRidge(): void {
    const root = new THREE.Group();
    root.name = 'northernRedRockRidge';
    root.position.set(-4, sampleSavannaGroundHeight(-4, 49), 49);
    for (let index = 0; index < 9; index += 1) {
      const rock = this.mesh(new THREE.IcosahedronGeometry(1.7 + (index % 3) * 0.48, 1), this.materials.redRock, 'ridgeRock');
      rock.position.set((index - 4) * 2.45, 1.2 + (index % 4) * 0.54, Math.sin(index * 1.8) * 2.2);
      rock.scale.set(1.1 + (index % 2) * 0.45, 1.5 + (index % 3) * 0.38, 0.86);
      rock.rotation.set(index * 0.19, index * 0.61, index * 0.08);
      root.add(rock);
    }
    this.root.add(root);
    this.landmarks.push({ id: 'red-rock-ridge', root, position: root.position, radius: 14 });
  }

  private createTermiteField(): void {
    const root = new THREE.Group();
    root.name = 'easternTermiteField';
    root.position.set(45, sampleSavannaGroundHeight(45, 4), 4);
    const points = [
      new THREE.Vector2(0.72, 0), new THREE.Vector2(0.88, 0.6), new THREE.Vector2(0.52, 1.8),
      new THREE.Vector2(0.42, 3.0), new THREE.Vector2(0.1, 4.1),
    ];
    const moundGeometry = this.track(new THREE.LatheGeometry(points, 9));
    for (let index = 0; index < 8; index += 1) {
      const angle = index * 2.399963;
      const distance = 2.5 + (index % 4) * 1.65;
      const mound = this.mesh(moundGeometry, this.materials.termite, 'termiteMound');
      mound.position.set(Math.cos(angle) * distance, 0, Math.sin(angle) * distance);
      mound.scale.setScalar(0.62 + (index % 3) * 0.16);
      mound.rotation.y = angle;
      root.add(mound);
    }
    this.root.add(root);
    this.landmarks.push({ id: 'termite-field', root, position: root.position, radius: 10 });
  }

  private createTargetRings(): void {
    const positions: ReadonlyArray<readonly [number, number, number]> = [[8, -8, 0], [37, -23, 0.3], [-26, 17, -0.4]];
    positions.forEach(([x, z, yaw], index) => {
      const root = new THREE.Group();
      root.name = `targetRing${index}`;
      root.position.set(x, sampleSavannaGroundHeight(x, z), z);
      root.rotation.y = yaw;
      const ringMaterial = this.materials.reward.clone();
      this.privateMaterials.add(ringMaterial);
      const ring = this.mesh(new THREE.TorusGeometry(1.1, 0.12, 8, 32), ringMaterial, 'targetRing');
      ring.position.y = 1.85;
      const leftPost = this.mesh(new THREE.CylinderGeometry(0.08, 0.12, 1.85, 7), this.materials.bark, 'targetPost');
      leftPost.position.set(-1.1, 0.93, 0);
      const rightPost = leftPost.clone();
      rightPost.position.x = 1.1;
      root.add(ring, leftPost, rightPost);
      this.root.add(root);
      const visual: TargetRingVisual = {
        id: `target-${index}`,
        root,
        position: root.position,
        radius: 1.25,
        hit: false,
        setHit: (hit) => {
          visual.hit = hit;
          ringMaterial.color.copy(hit ? SAVANNA_SIGNAL_COLORS.healthy : SAVANNA_SIGNAL_COLORS.reward);
          ringMaterial.emissiveIntensity = hit ? 0.5 : 0.18;
        },
      };
      this.targetRings.push(visual);
    });
  }

  private createGrassField(count: number): THREE.InstancedMesh {
    const geometry = this.track(new THREE.ConeGeometry(0.09, 0.72, 4));
    geometry.translate(0, 0.36, 0);
    const material = new THREE.MeshStandardMaterial({
      color: 0xb3a056,
      roughness: 1,
      metalness: 0,
      vertexColors: true,
    });
    this.privateMaterials.add(material);
    const grass = new THREE.InstancedMesh(geometry, material, count);
    grass.name = 'instancedDryGrass';
    grass.receiveShadow = true;
    grass.castShadow = false;
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const position = new THREE.Vector3();
    for (let index = 0; index < count; index += 1) {
      const x = ((index * 37) % 113) - 56;
      const z = ((index * 67) % 109) - 54;
      const jitterX = Math.sin(index * 12.9898) * 1.6;
      const jitterZ = Math.sin(index * 78.233) * 1.6;
      position.set(x + jitterX, sampleSavannaGroundHeight(x + jitterX, z + jitterZ), z + jitterZ);
      quaternion.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, index * 2.399963);
      const size = 0.65 + ((index * 17) % 13) / 13 * 0.72;
      scale.set(size, size, size);
      matrix.compose(position, quaternion, scale);
      grass.setMatrixAt(index, matrix);
      const hue = 0.12 + (index % 7) * 0.004;
      grass.setColorAt(index, new THREE.Color().setHSL(hue, 0.38, 0.42 + (index % 3) * 0.035));
    }
    grass.instanceMatrix.needsUpdate = true;
    if (grass.instanceColor) grass.instanceColor.needsUpdate = true;
    grass.computeBoundingSphere();
    return grass;
  }

  private branchBetween(start: THREE.Vector3, end: THREE.Vector3, radius: number, material: THREE.Material): THREE.Mesh {
    const length = start.distanceTo(end);
    const branch = this.mesh(new THREE.CylinderGeometry(radius * 0.68, radius, length, 7), material, 'treeBranch');
    branch.position.copy(start).add(end).multiplyScalar(0.5);
    branch.quaternion.setFromUnitVectors(THREE.Object3D.DEFAULT_UP, end.clone().sub(start).normalize());
    return branch;
  }

  private mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
    this.track(geometry);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.castShadow = material !== this.materials.groundContact;
    mesh.receiveShadow = true;
    return mesh;
  }

  private track<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.add(geometry);
    return geometry;
  }
}

export function createSavannaWorld(options: SavannaWorldOptions = {}): SavannaWorldVisuals {
  return new SavannaWorldVisuals(options);
}

export function sampleSavannaGroundHeight(x: number, z: number): number {
  const broad = Math.sin(x * 0.055) * 0.34 + Math.cos(z * 0.047) * 0.29;
  const detail = Math.sin((x + z) * 0.12) * 0.09;
  const dx = x - 29;
  const dz = (z + 28) / 0.72;
  const waterDistance = Math.sqrt(dx * dx + dz * dz);
  const basin = 1 - THREE.MathUtils.smoothstep(waterDistance, 7.5, 13.2);
  return broad + detail - basin * 1.15;
}
